// /en/visa/$nationality/$visatype — EN mirror of TH per-pair page (P-EN-Visa-Nationality-Type)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getVisaNationality, getVisaType } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ContactCTA } from "@/components/seo/Blocks";
import { Plane, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, Check, X } from "lucide-react";
import VisaTranslateDeepLinks from "@/components/VisaTranslateDeepLinks";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/visa/$nationality/$visatype")({
  loader: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    const v = getVisaType(params.visatype);
    if (!n || !v) throw notFound();
    if (!n.topVisaTypes.includes(v.id)) throw notFound();
    return { nat: params.nationality, vt: params.visatype };
  },
  head: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    const v = getVisaType(params.visatype);
    if (!n || !v) return { meta: [{ title: ctaTitle("Page not found", "/en/visa/$nationality/$visatype", "en")}] };
    const enPath = `/en/visa/${n.slug}/${v.id}`;
    const thPath = `/visa/${n.slug}/${v.id}`;
    const url = `${SITE}${enPath}`;
    const title = `${v.name} (${v.code}) for ${n.demonymEn} Citizens ${n.flag} · ${v.serviceFee} | NYC`;
    const desc = `${v.name} application service for ${n.demonymEn} nationals — ${v.tagline} · Service fee ${v.serviceFee} · Processing ${v.processingTime} · Validity ${v.durationInitial}. Ask us before you file — it's free.`;
    const og = dynamicOgImage({ title: `${v.code} · ${n.demonymEn}s`, subtitle: `${v.name} · ${v.processingTime}`, badge: "Thai Visa" });
    return {
      meta: [
        { title: ctaTitle(title, "/en/visa/$nationality/$visatype", "en") }, { name: "description", content: ctaDesc(desc, "/en/visa/$nationality/$visatype", "en")},
        { name: "keywords", content: `${v.name} ${n.demonymEn}, ${v.code} visa ${n.demonymEn}, Thai ${v.code} for ${n.countryEn}, ${v.name} Thailand` },
        { property: "og:title", content: ctaTitle(title, "/en/visa/$nationality/$visatype", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/visa/$nationality/$visatype", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { property: "og:image", content: og }, { name: "twitter:image", content: og },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${v.name} for ${n.demonymEn} nationals`,
              serviceType: `${v.name} application for ${n.demonymEn} citizens`,
              inLanguage: "en",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price: v.serviceFee.replace(/[^\d]/g, "").slice(0, 6) || "25000", priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `How to apply for ${v.name} (${v.code}) as a ${n.demonymEn} citizen`,
              inLanguage: "en", totalTime: "P10D",
              step: [
                { "@type": "HowToStep", position: 1, name: "Free eligibility check", text: "Send your passport and supporting documents via LINE @nycli — free assessment within 24h." },
                { "@type": "HowToStep", position: 2, name: "Prepare documents", text: `Gather ${v.documents.slice(0, 3).join(", ")}.` },
                { "@type": "HowToStep", position: 3, name: "File at Embassy / BOI / Immigration", text: `We file on your behalf and track the application — typical turnaround ${v.processingTime}.` },
                { "@type": "HowToStep", position: 4, name: "Receive visa", text: `Visa valid for ${v.durationInitial}.` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Thai Visa", item: `${SITE}/en/visa` },
                { "@type": "ListItem", position: 3, name: `${n.demonymEn} citizens`, item: `${SITE}/en/visa/${n.slug}` },
                { "@type": "ListItem", position: 4, name: v.name, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnVisaPairPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/visa" className="mt-4 inline-block text-gold underline">Back to all Thai visas</Link>
    </main>
  ),
});

// Add-on language + apostille mapping (mirrors TH page)
const LANG_MAP: Record<string, string> = {
  american: "english", canadian: "english", british: "english", australian: "english", "new-zealander": "english", irish: "english", "south-african": "english",
  german: "german", austrian: "german", swiss: "german",
  french: "french", belgian: "french",
  spanish: "spanish", mexican: "spanish", argentinian: "spanish", chilean: "spanish", colombian: "spanish", peruvian: "spanish",
  brazilian: "portuguese", portuguese: "portuguese",
  italian: "italian", russian: "russian", dutch: "dutch", swedish: "swedish", norwegian: "norwegian",
  chinese: "chinese", taiwanese: "chinese", "hong-konger": "chinese", singaporean: "chinese",
  japanese: "japanese", korean: "korean",
  vietnamese: "vietnamese", burmese: "burmese", cambodian: "khmer", malaysian: "malay-indonesian", indonesian: "malay-indonesian",
  saudi: "arabic", emirati: "arabic", qatari: "arabic", kuwaiti: "arabic",
};
const COUNTRY_MAP: Record<string, string> = {
  american: "usa", canadian: "canada", british: "uk", australian: "australia", "new-zealander": "new-zealand", irish: "ireland", "south-african": "south-africa",
  german: "germany", austrian: "austria", swiss: "switzerland",
  french: "france", belgian: "belgium", dutch: "netherlands",
  spanish: "spain", italian: "italy", portuguese: "portugal",
  swedish: "sweden", norwegian: "norway", danish: "denmark", finnish: "finland",
  russian: "russia", polish: "poland", czech: "czech", hungarian: "hungary", greek: "greece",
  mexican: "mexico", argentinian: "argentina", brazilian: "brazil", chilean: "chile", colombian: "colombia", peruvian: "peru",
  chinese: "china", japanese: "japan", korean: "south-korea", indian: "india", filipino: "philippines", singaporean: "singapore", "hong-konger": "hong-kong", israeli: "israel", turkish: "turkey",
  vietnamese: "vietnam", malaysian: "malaysia", indonesian: "indonesia", cambodian: "cambodia", burmese: "myanmar", laotian: "laos",
  emirati: "uae", saudi: "saudi-arabia", qatari: "qatar", kuwaiti: "kuwait", egyptian: "egypt",
};

function EnVisaPairPage() {
  const { nat, vt } = Route.useLoaderData() as { nat: string; vt: string };
  const n = getVisaNationality(nat)!;
  const v = getVisaType(vt)!;
  const otherVisas = n.topVisaTypes.filter((id) => id !== vt).slice(0, 6).map((id) => getVisaType(id)!).filter(Boolean);
  const addonLang = LANG_MAP[n.slug];
  const addonApostille = COUNTRY_MAP[n.slug];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/visa" className="hover:text-gold">Thai Visa</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/visa/$nationality" params={{ nationality: n.slug }} className="hover:text-gold">{n.demonymEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.code}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> {v.code} · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            {v.name} for {n.demonymEn} Citizens
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{v.tagline}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Validity", v: v.durationInitial },
              { k: "Service fee", v: v.serviceFee },
              { k: "Processing", v: v.processingTime },
              { k: "Work permit", v: v.workPermit },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service files {v.name} ({v.code}) for {n.demonymEn} citizens — {v.tagline} Service fee {v.serviceFee}, processing {v.processingTime}. {n.visaExempt > 0 ? `(${n.demonymEn} passports enter Thailand visa-exempt for ${n.visaExempt} days.)` : `(${n.demonymEn} citizens must obtain a visa before travel at a Royal Thai Embassy.)`}
        </blockquote>

        <section className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6">
            <h2 className="font-display text-xl font-extrabold text-emerald-900">Advantages</h2>
            <ul className="mt-4 space-y-2">
              {v.pros.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-slate-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-6">
            <h2 className="font-display text-xl font-extrabold text-rose-900">Limitations</h2>
            <ul className="mt-4 space-y-2">
              {v.cons.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-slate-800">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Required documents</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {v.documents.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Package pricing</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {v.name} · {v.serviceFee}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Document preparation + on-your-behalf filing + tracking</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Turnaround within {v.processingTime}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Government fee {v.govFee}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Free re-filing guarantee if rejected due to our error</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {otherVisas.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other visas for {n.demonymEn} citizens</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherVisas.map((o) => (
                <Link key={o.id} to="/en/visa/$nationality/$visatype" params={{ nationality: n.slug, visatype: o.id }}
                  className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{o.code}</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.name}</div>
                    <div className="mt-1 text-[11px] text-slate-500">{o.tagline}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {(addonLang || addonApostille) && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              Add-on services {n.demonymEn} applicants often pair with {v.code}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              All-in-one document workflow — certified translation · MFA legalization · Apostille · Notary Public.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {addonLang && (
                <Link to="/translate/$language" params={{ language: addonLang }}
                  className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Certified translation</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      Translate documents for {n.demonymEn} use
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">Sworn certification + MFA stamp</div>
                  </div>
                </Link>
              )}
              {addonApostille && (
                <Link to="/apostille/$country" params={{ country: addonApostille }}
                  className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Apostille / Legalization</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      Legalize documents for use in {n.countryEn}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">MFA + embassy / Hague Apostille</div>
                  </div>
                </Link>
              )}
              <Link to="/notary"
                className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Notary Public</div>
                  <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    Notarial Services Attorney
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">Signature certification · true copy</div>
                </div>
              </Link>
            </div>
          </section>
        )}

        <VisaTranslateDeepLinks
          mode="en"
          visaTypeId={v.id}
          visaCode={v.code}
          language={addonLang}
          languageNameTh=""
          languageNameEn={addonLang ? addonLang.charAt(0).toUpperCase() + addonLang.slice(1) : ""}
          nationalityNameTh={`ชาว${n.nameTh}`}
          nationalityDemonymEn={n.demonymEn}
        />

        <section className="mt-14 border-t border-slate-200 pt-10 text-sm text-slate-600">
          Looking for the Thai version? <Link to="/visa/$nationality/$visatype" params={{ nationality: nat, visatype: vt }} className="font-semibold text-amber-700 underline">/visa/{nat}/{vt}</Link>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
